import { create } from 'zustand';
import {
  devtools,
  persist,
  subscribeWithSelector,
  createJSONStorage,
  type PersistStorage,
} from 'zustand/middleware';
import { createTrackedSelector } from 'react-tracked';
import { produce } from 'immer';
import { pick } from 'lodash';
import { temporaryDashboardDb } from '@shared/utils/dashboard';
import { buildCacheKeyPredicate } from '@shared/utils/cache';
import { removeFunnelAnalysisQuery } from '@src/services/queries';
import { cacheStore } from './cache';

type StaticPageNav = {
  id: StaticPageID;
  type: StaticPageType;
  name: string;
  path: string;
  search?: string;
  state?: Record<string, any>;
};

type DashboardPageNav = Pick<AnalysisDashboard, 'id' | 'type'> & {
  name: string;
  search?: string;
  state?: Record<string, any>;
};

type PageNav = StaticPageNav | DashboardPageNav;

type NavData<T extends string> = T extends StaticPageID ? StaticPageNav : DashboardPageNav;

export interface PageNavsStore {
  navs: PageNav[];
  nextTempCount: number;
  upsert: (value: PageNav) => void;
  patch: <T extends string>(id: T, data: Omit<Partial<NavData<T>>, 'id' | 'type'>) => void;
  remove: (id: string) => void;
  find: (id: string) => PageNav | undefined;
  getNextTempCount: () => number;
  getRoutePath: (item: PageNav) => string | undefined;
  getRoutePathById: (id: string) => string | undefined;
}

const createSyncedStorage = (): PersistStorage<any> => {
  const storage = createJSONStorage(() => localStorage)!;

  /**
   * Cross-tab synchronization using BroadcastChannel API
   * Disabled for now but can be enabled in the future if needed
   */

  // const channel = new BroadcastChannel('dashboard-store');

  // channel.onmessage = (event) => {
  //   if (event.data?.name === dashboardStore.persist.getOptions().name) {
  //     void dashboardStore.persist.rehydrate();
  //   }
  // };

  return {
    ...storage,
    setItem: async (name, value) => {
      await storage.setItem(name, value);
      // channel.postMessage({ name });
    },
    removeItem: async (name) => {
      await storage.removeItem(name);
      // channel.postMessage({ name });
    },
  };
};

const DEFAULT_PAGES: StaticPageNav[] = [
  {
    id: 'explorer',
    type: 'static',
    name: 'Pattern Analytics',
    path: '/paths',
  },
  {
    id: 'management',
    type: 'static',
    name: 'Management',
    path: '/management',
  },
];

const DEFAULT_TEMP_COUNT = 1;

export const pageNavsStore = create<PageNavsStore>()(
  devtools(
    persist(
      subscribeWithSelector((set, get) => ({
        navs: [...DEFAULT_PAGES],
        nextTempCount: DEFAULT_TEMP_COUNT,
        upsert: (value) => {
          const existingIndex = get().navs.findIndex(
            (d) => d.id === value.id && d.type === value.type,
          );

          // Insert new dashboard
          if (existingIndex === -1 && value.type !== 'static') {
            set(
              {
                navs: [
                  ...get().navs,
                  pick(value, ['id', 'type', 'name', 'state', 'search']) as DashboardPageNav,
                ],
              },
              undefined,
              `pageNavsStore/insert`,
            );
          }

          // Update static page
          if (existingIndex !== -1 && value.type === 'static') {
            set(
              {
                navs: produce(get().navs, (draft) => {
                  draft[existingIndex] = {
                    ...draft[existingIndex],
                    ...pick(value, ['path', 'state', 'search']),
                  };
                }),
              },
              undefined,
              `pageNavsStore/updateStaticPage/${value.id}`,
            );
          }

          // Update dashboard
          if (existingIndex !== -1 && value.type !== 'static') {
            set(
              {
                navs: produce(get().navs, (draft) => {
                  draft[existingIndex] = {
                    ...draft[existingIndex],
                    ...pick(value, ['name', 'state', 'search']),
                  };
                }),
              },
              undefined,
              `pageNavsStore/updateDashboard/${value.id}`,
            );
          }
        },
        patch: (id, value) => {
          const existingIndex = get().navs.findIndex((d) => d.id === id);

          if (existingIndex !== -1) {
            set(
              {
                navs: produce(get().navs, (draft) => {
                  const existing = draft[existingIndex];
                  draft[existingIndex] = {
                    ...existing,
                    ...pick(
                      value,
                      existing.type === 'static'
                        ? ['path', 'state', 'search']
                        : ['name', 'state', 'search'],
                    ),
                  };
                }),
              },
              undefined,
              `pageNavsStore/patch/${id}`,
            );
          }
        },
        remove: (id) => {
          const targetIndex = get().navs.findIndex((d) => d.id === id);

          if (targetIndex === -1) return;

          const target = get().navs[targetIndex];

          set(
            {
              navs: get().navs.filter((d) => d.id !== id),
            },
            undefined,
            `pageNavsStore/remove/${id}`,
          );

          // Remove temporary dashboard from IndexedDB
          if (target.type === 'temp') {
            void temporaryDashboardDb.removeItem(id);
          }

          // Invalidate related funnel analysis cache in React Query
          if (target.type === 'saved') {
            removeFunnelAnalysisQuery(id);
          }

          // Clean up related cache
          cacheStore.getState().removeWithPredicate(buildCacheKeyPredicate(id));
        },
        find: (id) => get().navs.find((d) => d.id === id),
        getNextTempCount: () => {
          const { nextTempCount } = get();

          set(
            {
              nextTempCount: nextTempCount + 1,
            },
            undefined,
            `pageNavsStore/incrementTempCount`,
          );

          return nextTempCount;
        },
        getRoutePath: (item) => {
          if (item.type === 'static') return item.path;

          if (item.type === 'saved' || item.type === 'temp') {
            return `/funnels/${item.type}/${item.id}`;
          }

          return;
        },
        getRoutePathById: (id) => {
          const item = get().navs.find((d) => d.id === id);

          if (!item) return;

          if (item.type === 'static') return item.path;

          return `/funnels/${item.type}/${item.id}`;
        },
      })),
      {
        name: 'user_journey_dashboards:{placeholder_customerId}',
        storage: createSyncedStorage(),
        partialize: (state) => ({
          navs: state.navs
            .filter((d) => d.type !== 'static')
            .map(({ id, type, name }) => ({ id, type, name })),
          nextTempCount: state.nextTempCount,
        }),
        merge: (persisted, currentState) => {
          const p = pick(persisted as { nextTempCount: number; navs: DashboardPageNav[] }, [
            'nextTempCount',
            'navs',
          ]);

          return {
            ...currentState,
            navs: [...DEFAULT_PAGES, ...(p.navs ?? [])],
            nextTempCount: p.nextTempCount ?? currentState.nextTempCount,
          };
        },
        version: 0,
        skipHydration: true,
      },
    ),
  ),
);

pageNavsStore.subscribe(
  (state) => state.navs.some((d) => d.type === 'temp'),
  (hasTemp) => {
    if (!hasTemp) {
      pageNavsStore.setState({ nextTempCount: 1 });
    }
  },
);

export const usePageNavsStore = createTrackedSelector(pageNavsStore);
